import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import PortalAlert from "./PortalAlert";
import PortalSelect from "./PortalSelect";

function pad(n) {
  return String(n).padStart(2, "0");
}

function toDateInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const raw = String(value).trim();
    return raw.length >= 10 ? raw.slice(0, 10) : "";
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toTimeInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const raw = String(value).trim().replace(" ", "T");
    if (raw.includes("T") && raw.length >= 16) return raw.slice(11, 16);
    return "";
  }
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function combineDateTime(dateValue, timeValue) {
  if (!dateValue) return null;
  const time = timeValue || "09:00";
  return `${dateValue} ${time.length === 5 ? `${time}:00` : time}`;
}

function nowLocalDateTime() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Compact follow-up update modal for the leads list.
 */
function PortalLeadQuickFollowupModal({
  lead,
  statuses = [],
  onClose,
  onSaved,
}) {
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");
  const [statusId, setStatusId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!lead) return;
    const next = lead.Last_Next_Followup_Date || "";
    setNextDate(toDateInputValue(next));
    setNextTime(toTimeInputValue(next) || "11:30");
    setStatusId(lead.Lead_Status_Id ? String(lead.Lead_Status_Id) : "");
    setNotes("");
    setError("");
  }, [lead]);

  const currentStatus = useMemo(() => {
    if (!lead) return "—";
    return lead.Lead_Status || "—";
  }, [lead]);

  if (!lead) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) {
      setError("Notes are required.");
      return;
    }
    if (!nextDate) {
      setError("Next follow-up date is required.");
      return;
    }

    setSaving(true);
    setError("");
    const leadId = lead.Real_Estate_Lead_Id;
    const nextFollowup = combineDateTime(nextDate, nextTime);
    const nextStatusId = statusId ? Number(statusId) : null;

    try {
      await axiosInstance.post(`/client/leads/${leadId}/followups`, {
        Followup_Date: nowLocalDateTime(),
        Remarks: notes.trim(),
        Next_Followup_Date: nextFollowup,
      });

      if (nextStatusId && Number(lead.Lead_Status_Id) !== nextStatusId) {
        await axiosInstance.put(`/client/leads/${leadId}`, {
          Lead_Status_Id: nextStatusId,
        });
      }

      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Could not save follow-up.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cportal-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cportal-modal cportal-followup-compact-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-followup-title"
      >
        <div className="cportal-modal-header">
          <h3 id="quick-followup-title">Update Follow-up</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <form className="cportal-followup-compact-body" onSubmit={handleSubmit}>
          <PortalAlert type="error" message={error} onClear={() => setError("")} />

          <div className="cportal-followup-current-status">
            <span>Current status:</span>
            <strong>{currentStatus}</strong>
          </div>

          <div className="cportal-field">
            <label>
              Next Follow-up <span className="cportal-required">*</span>
            </label>
            <div className="cportal-followup-datetime-row">
              <input
                type="date"
                value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                required
              />
              <input
                type="time"
                value={nextTime}
                onChange={(e) => setNextTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="cportal-field">
            <label>Status</label>
            <PortalSelect value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              <option value="">Select Status</option>
              {statuses.map((s) => (
                <option key={s.Status_Id} value={s.Status_Id}>
                  {s.Status}
                </option>
              ))}
            </PortalSelect>
          </div>

          <div className="cportal-field">
            <label>
              Notes <span className="cportal-required">*</span>
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Call customer after 11 AM…"
              required
            />
          </div>

          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Follow-up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PortalLeadQuickFollowupModal;
